import { Select } from "@suraasa/placebo-ui"
import { useQueryClient } from "@tanstack/react-query"
import api from "api"
import { LeadProfile } from "api/resources/lead/types"
import { APIError } from "api/utils"
import { Controller, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { OptionsOrGroups } from "react-select"
import { toast } from "react-toastify"
import { mapErrors } from "utils/helpers"

type Option = {
  label: string
  value: string | number
}

type SelectValue = Option | Option[] | null

type Props = {
  saveKey: string
  value: SelectValue
  label: string
  options: OptionsOrGroups<Option, any>
  isMulti?: boolean
  disableAutoSave?: boolean
  onUpdate?: (value: SelectValue) => void
  [x: string]: any
}

export const formatSelectValue = (
  value: string | number | null | undefined | string[] | number[],
  options?: Option[]
) => {
  if (value == null) return []

  const getOptionLabel = (id: string | number) =>
    (options?.find(x => x.value === value)?.label ?? id).toString()

  if (typeof value === "string" || typeof value === "number")
    return [{ value, label: getOptionLabel(value) }]

  if (value.length === 0) return []

  return value.map(x => ({ value: x, label: getOptionLabel(x) }))
}

const AutoSaveSelect = (props: Props) => {
  const { userId } = useParams()
  const queryClient = useQueryClient()

  const {
    saveKey,
    label,
    userPrimaryKey,
    value,
    isMulti,
    options,
    disableAutoSave,
    onUpdate,
    ...selectProps
  } = props

  const {
    formState: { errors },
    setError,
    control,
  } = useForm({
    defaultValues: {
      [saveKey]: value,
    },
  })

  const save = async (value: Option | Option[] | null) => {
    if (!isMulti && Array.isArray(value)) {
      value = value[0] ?? null
    }

    if (onUpdate) {
      onUpdate(value)
    }

    if (disableAutoSave) {
      return
    }

    const data = {
      [saveKey]: Array.isArray(value) ? value.map(x => x.value) : value?.value,
    }

    try {
      await api.lead.saveData({
        data,
        params: {
          userPrimaryKey,
        },
      })

      // @ts-expect-error this is safe to do
      queryClient.setQueryData<LeadProfile>(["profile", userId], oldData => ({
        ...(oldData ?? {}),
        ...data,
      }))
    } catch (error) {
      if (error instanceof APIError) {
        const err = error.errors

        if (err.message) {
          toast.error(err.message)
          return
        }
        if (err.fieldErrors) {
          mapErrors(setError, err, [[saveKey]])
          return
        }
      }
    }
  }

  return (
    <Controller
      control={control}
      name={saveKey}
      render={({ field: { onChange, value } }) => {
        return (
          <Select
            className="col-span-1"
            // @ts-expect-error this is fine
            errors={errors[saveKey]?.message}
            getOptionLabel={item => item.label}
            getOptionValue={item => item.value.toString()}
            label={label}
            options={options}
            value={value}
            fullWidth
            isMulti={isMulti}
            onChange={val => {
              onChange(val)
              // @ts-expect-error types issue but safe to do
              save(val)
            }}
            {...selectProps}
          />
        )
      }}
    />
  )
}

export default AutoSaveSelect
